import React, { useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Card, Skel } from './parts';
import { Globe, GlobeHome, GlobePoint } from './Globe';
import { City, int, Visitors } from './api';

// Outpost Custom's showroom, Kidderminster: the arcs start here.
const HOME: GlobeHome = { lat: 52.3877, lng: -2.2497, label: 'Showroom' };
/** Great-circle distance from the showroom (haversine). */
const km = (c: City) => {
  const r = Math.PI / 180;
  const dLat = ((c.lat ?? 0) - HOME.lat) * r;
  const dLng = ((c.lng ?? 0) - HOME.lng) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(HOME.lat * r) * Math.cos((c.lat ?? 0) * r) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * 6371 * Math.asin(Math.sqrt(a)));
};
/** cobe draws every arc at one height, so short hops would be tall spikes: only towns further than this get one. */
const ARC_MIN_KM = 300;
const idOf = (c: City) => `${c.city}|${c.country}`;

/**
 * Where visits came from, on a globe that fills the card edge to edge, with the top towns in a
 * panel over it. Hovering a town in the list turns the globe to it. Locations are PostHog GeoIP,
 * so they are the visitor's network's town, not an exact address.
 */
export const PlacesCard: React.FC<{ v: Visitors | null; loading: boolean }> = ({ v, loading }) => {
  const [focus, setFocus] = useState<string | null>(null);
  const total = Math.max(1, v?.totals.sessions ?? 0);
  const named = useMemo(() => (v?.cities ?? []).filter((c) => c.city && c.lat != null && c.lng != null), [v]);
  const points: GlobePoint[] = useMemo(() => {
    const max = Math.max(1, ...named.map((c) => c.sessions));
    return named.map((c) => ({
      id: idOf(c),
      lat: c.lat as number,
      lng: c.lng as number,
      weight: c.sessions / max,
      title: `${c.city}, ${c.country}`,
      sub: `${int(c.sessions)} session${c.sessions === 1 ? '' : 's'} · ${Math.round((c.sessions / total) * 100)}% · ${int(km(c))} km from the showroom`,
      arc: km(c) > ARC_MIN_KM,
    }));
  }, [named, total]);

  if (!v) {
    return (
      <Card i={5} area="geo" title="Places">
        {loading ? <Skel className="min-h-[480px] flex-1" /> : <p className="oa-cap">Places appear once PostHog is connected.</p>}
      </Card>
    );
  }

  const countries = v.countries ?? [];
  const top = countries[0];
  const unplaced = v.cities.filter((c) => !c.city).reduce((a, c) => a + c.sessions, 0);
  const furthest = named.reduce<City | null>((best, c) => (!best || km(c) > km(best) ? c : best), null);
  const list = named.slice(0, 6);
  const listMax = Math.max(1, ...list.map((c) => c.sessions));

  return (
    <Card
      i={5}
      area="geo"
      className="oa-geo"
      title="Places"
      actions={
        countries.length || named.length ? (
          <span className="oa-chip is-plain">
            {countries.length ? `${countries.length} ${countries.length === 1 ? 'country' : 'countries'} · ` : ''}
            {named.length} towns
          </span>
        ) : null
      }
    >
      {points.length ? <Globe points={points} home={HOME} focus={focus} /> : null}

      {named.length === 0 ? (
        <p className="oa-cap relative z-[1]">No visits with a known location in this period.</p>
      ) : (
        <>
          <div className="oa-geo-head">
            {top ? (
              <>
                <span className="oa-big text-[40px]">{Math.round((top.sessions / total) * 100)}%</span>
                <span className="oa-cap">of visits from {top.name || top.code}</span>
              </>
            ) : null}
            {furthest && km(furthest) > 50 ? (
              <span className="text-[12px] text-[var(--oa-faint)]">
                Furthest: {furthest.city}, {int(km(furthest))} km away
              </span>
            ) : null}
          </div>

          <div className="oa-geo-panel">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="oa-sub">Top towns</h3>
              {unplaced ? <span className="text-[11px] text-[var(--oa-faint)]">{int(unplaced)} not placed</span> : null}
            </div>
            <ol className="oa-bars" onMouseLeave={() => setFocus(null)}>
              {list.map((c, i) => (
                <li key={idOf(c)} className={cn(focus === idOf(c) && 'is-on')} style={{ ['--i' as string]: i } as React.CSSProperties} onMouseEnter={() => setFocus(idOf(c))}>
                  <span className="truncate">
                    {c.city} <small className="text-[var(--oa-faint)]">{c.country}</small>
                  </span>
                  <b>{Math.round((c.sessions / total) * 100)}%</b>
                  <i style={{ ['--w' as string]: `${(c.sessions / listMax) * 100}%` } as React.CSSProperties} />
                  <em>{int(km(c))} km</em>
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-[var(--oa-faint)]">Drag the globe to turn it. Towns come from IP addresses, so they are approximate.</p>
          </div>
        </>
      )}
    </Card>
  );
};
